import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react';
import type { GoalCategory } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { getErrorMessage } from '@/lib/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable, type Column } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * Goal categories live inside the Goals module (module-scoped config — there is no
 * global master page). Deactivating keeps historical goals labelled.
 */
export function GoalCategoriesTab() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deactivateTarget, setDeactivateTarget] = useState<GoalCategory | null>(null);

  const listQuery = useQuery({
    queryKey: ['goals', 'categories', 'all'],
    queryFn: () => goalsService.listCategories(false),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['goals', 'categories'] });
  }

  const createMutation = useMutation({
    mutationFn: () => goalsService.createCategory({ name, description: description || null }),
    onSuccess: () => {
      toast.success('Category added');
      invalidate();
      setName('');
      setDescription('');
      setCreateOpen(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => goalsService.deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deactivated');
      invalidate();
      setDeactivateTarget(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => goalsService.updateCategory(id, { is_active: true }),
    onSuccess: () => {
      toast.success('Category reactivated');
      invalidate();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const columns: Column<GoalCategory>[] = [
    {
      key: 'name',
      header: 'Category',
      cell: (row) => <span className="text-sm font-medium text-foreground">{row.name}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      cell: (row) => (
        <span className="text-sm text-muted-foreground">{row.description ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) =>
        row.is_active
          ? <Badge variant="success">Active</Badge>
          : <Badge variant="secondary">Inactive</Badge>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-20 text-right',
      cell: (row) =>
        row.is_active ? (
          <button
            type="button"
            aria-label={`Deactivate ${row.name}`}
            onClick={() => setDeactivateTarget(row)}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            aria-label={`Reactivate ${row.name}`}
            disabled={reactivateMutation.isPending}
            onClick={() => reactivateMutation.mutate(row.id)}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-primary"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Categories group goals by function so managers can filter and compare them.
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> Add Category
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={listQuery.data ?? []}
        rowKey={(r) => r.id}
        loading={listQuery.isLoading}
        error={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        empty="No categories yet"
      />

      <Dialog open={createOpen} onOpenChange={(v) => !v && setCreateOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
            <DialogDescription>Used to group and filter goals.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="category-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="category-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Content Production"
                maxLength={100}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="category-description">Description</Label>
              <Textarea
                id="category-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!name.trim() || createMutation.isPending}
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deactivateTarget}
        onOpenChange={(v) => !v && setDeactivateTarget(null)}
        title={`Deactivate ${deactivateTarget?.name ?? 'category'}?`}
        description="It disappears from the pickers. Existing goals keep the label and it can be reactivated later."
        confirmLabel="Deactivate"
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
      />
    </div>
  );
}
